import { DotsThreeIcon, type Icon } from '@phosphor-icons/react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MENU_ITEM_MOBILE, MOBILE_HIT_AREA, MOBILE_ICON_BUTTON_XS } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * CardActionMenu — the canonical hover-reveal overflow menu for content cards.
 *
 * Shared by the Library artifact cards (`LibraryPage`) AND the Faces cards
 * (`FaceCard`) so the two sibling surfaces under the Library nav never drift.
 * Per design system §Sibling-surface parity: one dots trigger, top-right, one
 * popover — never a scatter of single-purpose icon buttons across both corners.
 *
 * The trigger's hover-visibility is owned by the CALL SITE (grid wraps it in an
 * `opacity-0 group-hover:opacity-100` box; the list row passes `triggerClassName`)
 * so the menu can adapt to whichever hover container it lives in.
 */
export function CardActionMenu({
  label,
  onThumbnail,
  align = 'end',
  triggerClassName,
  children,
}: {
  /** Accessible label for the trigger, e.g. `More actions for ${title}`. */
  label: string;
  /**
   * When the trigger overlays imagery (grid thumbnail, face photo) the flat dots
   * vanish against light media — give it a translucent backplate for legibility.
   */
  onThumbnail?: boolean;
  align?: 'start' | 'end' | 'center';
  /** Extra classes for the trigger button (hover-visibility lives here). */
  triggerClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="flat"
          size="toolbar-icon"
          className={cn(
            // Canonical card kebab: 40px touch target on mobile, 28px on desktop
            // (matches the Workflows card trigger). toolbar-icon supplies
            // p-0 · rounded-full · [&_svg]:size-4.
            MOBILE_ICON_BUTTON_XS,
            MOBILE_HIT_AREA,
            'shrink-0 hover:bg-muted/40 hover:text-foreground data-[state=open]:bg-muted/40 data-[state=open]:text-foreground',
            // A thumbnail control is a single quiet, translucent circle — the
            // same surface used by FaceCard's save control. The former pale
            // hover chip plus a large "More actions" tooltip made one small
            // kebab visually louder than the asset it belonged to.
            onThumbnail &&
              // eslint-disable-next-line no-restricted-syntax -- Thumbnail controls need fixed contrast over arbitrary media.
              'bg-black/40 text-white/85 backdrop-blur hover:bg-black/60 hover:text-white data-[state=open]:bg-black/60 data-[state=open]:text-white',
            triggerClassName,
          )}
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
        >
          <DotsThreeIcon className="size-5" weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="w-44 rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-elevation-3"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A single menu row. `destructive` tints it red (Delete/Remove); pass an `icon`
 * from Phosphor. Every call site gets identical padding + icon size.
 */
export function CardActionItem({
  icon: IconCmp,
  onSelect,
  destructive,
  disabled,
  children,
}: {
  icon: Icon;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      className={cn(
        MENU_ITEM_MOBILE,
        'rounded-sm',
        destructive && 'text-destructive focus:text-destructive',
      )}
      onSelect={() => onSelect()}
    >
      <IconCmp className="size-4" />
      <span>{children}</span>
    </DropdownMenuItem>
  );
}

/** A one-level nested submenu (for example, "Add to project" -> project list). */
export function CardActionSubmenu({
  icon: IconCmp,
  label,
  children,
}: {
  icon: Icon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className={cn(MENU_ITEM_MOBILE, 'rounded-sm')}>
        <IconCmp className="size-4" />
        <span>{label}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-64 w-48 overflow-y-auto">
        {children}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
